import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

import type { AgentType } from "clippyts/dist/types";
import clippyts, { Agent } from "clippyts";

import { useApp } from "../context/AppContext";

export { AgentType };

export enum AgentEnum {
  Clippy = "Clippy",
  Bonzi = "Bonzi",
  F1 = "F1",
  Genie = "Genie",
  Genius = "Genius",
  Links = "Links",
  Merlin = "Merlin",
  Peedy = "Peedy",
  Rocky = "Rocky",
  Rover = "Rover",
}

export type ClippyContextType = {
  currentAgent: AgentType | null;
  isAgentVisible: boolean;

  showAgent: (agentName: string, fast?: boolean) => void;
  hideAgent: (fast?: boolean) => void;
  showMessage: (message: string, timeoutMs?: number) => void;
  playAnimation: (animationName?: string) => void;
  cancelAnimation(): void;
  moveTo(x: number, y: number): void;
  gestureAt(x: number, y: number): void;
};

const ClippyContext = createContext<ClippyContextType | undefined>(undefined);

export const useClippy = () => useContext(ClippyContext);
export const useAgent = () => useContext(ClippyContext);

export default function ClippyProvider({ children }: { children: ReactNode }) {
  const { volume, sessionReady, onMessage } = useApp();

  // use ref to avoid triggering renders, track actual agent instance
  const agentRef = useRef<Agent | null>(null);

  const [currentAgent, setCurrentAgent] = useState<AgentType | null>(null);
  const [isAgentVisible, setAgentIsVisible] = useState<boolean>(false);
  const [isLoadingAgent, setIsLoadingAgent] = useState<boolean>(false);

  useEffect(() => {
    if (agentRef.current) {
      agentRef.current.setVolume(volume);
    }
  }, [volume]);

  // load agent helper
  const _loadAgent = (agentName: AgentType, fast: boolean = false) => {
    setIsLoadingAgent(true);

    clippyts.load({
      name: agentName,
      successCb: (agent: Agent) => {
        agent.setVolume(volume);
        agent.show(fast);

        agentRef.current = agent;
        setCurrentAgent(agentName);
        setAgentIsVisible(true);
        setIsLoadingAgent(false);
      },

      failCb: (error) => {
        console.error(error);
        agentRef.current = null;
        setCurrentAgent(null);
        setAgentIsVisible(false);
        setIsLoadingAgent(false);
      },
    });
  };

  const _unloadAgent = (fast: boolean, cb?: () => void) => {
    function newCb() {
      agentRef.current = null;
      setCurrentAgent(null);
      setAgentIsVisible(false);
      if (cb) cb();
    }

    if (agentRef.current) {
      agentRef.current.hide(fast, newCb);
    } else {
      newCb();
    }
  };

  /// PUBLIC

  const showAgent = useCallback(
    (agentName: AgentType, fast: boolean = false) => {
      // validate agentname in enum
      if (!Object.keys(AgentEnum).includes(agentName)) {
        console.warn("ClippyProvider: invalid agent name", agentName);
        return;
      }

      // prevent concurrent loads
      if (isLoadingAgent) return;

      // if same agent already loaded, do nothing
      if (currentAgent === agentName && isAgentVisible) return;

      // unload current agent and load new one
      if (agentRef.current) {
        _unloadAgent(false, () => {
          _loadAgent(agentName, fast);
        });
      } else {
        _loadAgent(agentName, fast);
      }
    },
    [],
  );

  const hideAgent = (fast: boolean = false) => {
    if (agentRef.current) {
      _unloadAgent(fast);
    }
  };

  const showMessage = (message: string, timeoutMs?: number) => {
    if (agentRef.current) {
      agentRef.current.speak(message, true);

      if (timeoutMs > 0) {
        setTimeout(() => {
          if (agentRef.current) {
            agentRef.current.closeBalloon();
          }
        }, timeoutMs);
      }
    }
  };

  const playAnimation = (animationName?: string) => {
    if (agentRef.current) {
      if (animationName) {
        console.log("playing specific animation", animationName);
        agentRef.current.play(animationName);
      } else {
        console.log("playing random animation");
        agentRef.current.animate();
      }
    }
  };

  const cancelAnimation = () => {
    if (agentRef.current) {
      agentRef.current.stopCurrent();
      agentRef.current.stop();
    }
  };

  const moveTo = (x: number, y: number, durationMs: number = 1000) => {
    if (agentRef.current) {
      agentRef.current.moveTo(x, y, durationMs);
    }
  };

  const gestureAt = (x: number, y: number) => {
    if (agentRef.current) {
      agentRef.current.gestureAt(x, y);
    }
  };

  ////// LISTENERS

  // attach/detach click handlers when agent changes
  useEffect(() => {
    const agent = agentRef.current;
    if (!agent) return;

    const onClick = () => {
      console.log("click detected");
      cancelAnimation();
      playAnimation();
    };

    const onDoubleClick = () => {
      console.log("double click detected");
      cancelAnimation();
      playAnimation();
    };

    agent.addEventListener("click", onClick);
    agent.addEventListener("dblclick", onDoubleClick);

    return () => {
      agent.removeEventListener("click", onClick);
      agent.removeEventListener("dblclick", onDoubleClick);
    };
  }, [isAgentVisible, currentAgent]);

  // listen for server messages (greetings, agent commands)
  useEffect(() => {
    if (!sessionReady) return;

    // handle greetings
    const unsubGreeting = onMessage("greeting", (msg: any) => {
      const greetingChar = msg.payload?.character;
      const greetingMsg = msg.payload?.message || "hello!";
      const greetingAnim = msg.payload?.animation || "Wave";

      showAgent(greetingChar || "Clippy");

      setTimeout(() => {
        showMessage(greetingMsg, 7000);
        playAnimation(greetingAnim);
      }, 100);
    });

    // handle agent service messages (commands)
    const unsubPlay = onMessage("play", (msg: any) => {
      playAnimation(msg.payload?.animation);
    });

    const unsubShow = onMessage("show", (msg: any) => {
      showAgent(msg.payload?.character || "Clippy");
    });

    const unsubHide = onMessage("hide", () => {
      hideAgent();
    });

    const unsubToggle = onMessage("toggle", (msg: any) => {
      if (isAgentVisible) {
        hideAgent();
      } else {
        showAgent(msg.payload?.character || "Clippy");
      }
    });

    return () => {
      unsubGreeting();
      unsubPlay();
      unsubShow();
      unsubHide();
      unsubToggle();
    };
  }, [sessionReady, onMessage]);

  return (
    <ClippyContext.Provider
      value={{
        currentAgent,
        isAgentVisible,

        showAgent,
        hideAgent,
        showMessage,
        playAnimation,
        cancelAnimation,
        moveTo,
        gestureAt,
      }}
    >
      {children}
    </ClippyContext.Provider>
  );
}
